/**
 * The month view that sits at the top of Bookings.
 *
 * It was its own page until a practitioner pointed out that spotting a session here meant
 * leaving for Manage Bookings to do anything about it (owner, Jun 2026). Now it hands a
 * session back to the list below, and an empty day opens the scheduling form.
 *
 * It keeps its own fetch: the list below pages through history, while this needs the month
 * being looked at, however far ahead or behind that is.
 */
import { useState, useEffect, useMemo } from "react";
import {
  ChevronLeft, ChevronRight, Clock, User, Globe, CalendarPlus,
  Download, Calendar as CalendarIcon, Link2, Copy, Check as CheckIcon
} from "lucide-react";
import { apiFetch } from "../../pages/marketplace/api";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const STATUS_COLORS = {
  requested: { bg: "#fef9ea", border: "#e8d080", text: "#9a6010" },
  confirmed: { bg: "#eaf5f0", border: "#a0d8c4", text: "#1a5a44" },
  completed: { bg: "#eef4fd", border: "#b0cce8", text: "#1a50a0" },
  cancelled: { bg: "#fdf0f0", border: "#e8b0b0", text: "#a02828" },
};

function formatTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

export function formatDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function generateICS(booking) {
  const start = new Date(booking.scheduled_at);
  const end = new Date(start.getTime() + (booking.duration_minutes || 60) * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const clientName = booking.guest_name || booking.customer_name || "Client";

  const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//11/11 Collective Connection//EN
BEGIN:VEVENT
UID:${booking.id}@eleven-collective.com
DTSTAMP:${fmt(new Date())}
DTSTART:${fmt(start)}
DTEND:${fmt(end)}
SUMMARY:${booking.session_title} — ${clientName}
DESCRIPTION:Session: ${booking.session_title}\\nDuration: ${booking.duration_minutes || 60} min\\nClient: ${clientName}${booking.notes ? "\\nNotes: " + booking.notes : ""}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${booking.session_title.replace(/[^a-z0-9]/gi, "_")}_${formatDateKey(start)}.ics`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

const styles = `
  .pc-wrap {
    --sand: #faf7f2; --teal: #1a5a44; --teal-bg: #eaf5f0; --white: #ffffff;
    --border: #e4ddd4; --ink: #1a1a16; --ink-mid: #5a5a54; --ink-light: #8a8a80;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 18px;
    overflow: hidden;
  }
  .pc-bar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; flex-wrap: wrap; padding: 12px 14px;
    border-bottom: 1px solid var(--border); background: var(--sand);
  }
  .pc-nav { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .pc-nav-btn {
    background: var(--white); border: 1px solid var(--border); border-radius: 6px;
    padding: 6px 10px; cursor: pointer; display: flex; align-items: center; gap: 4px;
    font-size: 13px; color: var(--ink-mid); transition: border-color 0.15s, color 0.15s;
  }
  .pc-nav-btn:hover { border-color: var(--teal); color: var(--ink); }
  .pc-month-label {
    font-family: 'Literata', serif; font-size: 15px; font-weight: 600;
    min-width: 128px; text-align: center; color: var(--ink);
  }
  .pc-today-btn {
    background: var(--teal); color: #fff; border: none; border-radius: 6px;
    padding: 6px 14px; cursor: pointer; font-size: 13px; font-weight: 600;
  }
  .pc-grid-wrap { padding: 12px 14px; overflow-x: auto; }
  .pc-day-headers {
    display: grid; grid-template-columns: repeat(7, minmax(40px, 1fr));
    gap: 1px; margin-bottom: 4px; min-width: 320px;
  }
  .pc-day-header {
    text-align: center; font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.5px; color: var(--ink-light); padding: 6px 0;
  }
  .pc-grid {
    display: grid; grid-template-columns: repeat(7, minmax(40px, 1fr)); gap: 1px;
    background: var(--border); border-radius: 8px; overflow: hidden; min-width: 320px;
  }
  .pc-cell {
    background: var(--white); min-height: 56px; padding: 3px 4px; cursor: pointer;
    position: relative; transition: background 0.15s;
  }
  .pc-cell:hover { background: #fefdfb; }
  .pc-cell.other-month { background: var(--sand); opacity: 0.5; }
  .pc-cell.today { box-shadow: inset 0 0 0 2px var(--teal); }
  .pc-cell.selected { background: var(--teal-bg); }
  .pc-cell-num { font-size: 12px; font-weight: 600; color: var(--ink-mid); margin-bottom: 2px; }
  .pc-cell.today .pc-cell-num { color: var(--teal); font-weight: 700; }
  .pc-cell-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: 2px; }
  .pc-cell-session {
    font-size: 9px; padding: 1px 3px; border-radius: 3px; margin-bottom: 1px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer;
  }
  .pc-cell-more { font-size: 9px; color: var(--ink-light); text-align: center; margin-top: 2px; }
  .pc-legend {
    display: flex; gap: 16px; padding: 10px 14px 0; font-size: 11px;
    color: var(--ink-light); flex-wrap: wrap;
  }
  .pc-legend-item { display: flex; align-items: center; gap: 4px; }
  .pc-detail { border-top: 1px solid var(--border); padding: 14px; background: var(--sand); }
  .pc-detail-header {
    font-family: 'Literata', serif; font-size: 15px; font-weight: 600;
    margin-bottom: 10px; color: var(--ink);
    display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
  }
  .pc-session-card {
    background: var(--white); border: 1px solid var(--border); border-radius: 8px;
    padding: 10px 14px; margin-bottom: 8px; display: flex; align-items: center;
    gap: 12px; flex-wrap: wrap; transition: border-color 0.15s;
  }
  .pc-session-card:hover { border-color: var(--teal); }
  .pc-session-time { font-size: 14px; font-weight: 600; min-width: 76px; color: var(--teal); }
  .pc-session-info { flex: 1; min-width: 160px; }
  .pc-session-title { font-size: 14px; font-weight: 600; color: var(--ink); margin-bottom: 2px; }
  .pc-session-meta {
    font-size: 12px; color: var(--ink-mid); display: flex; gap: 12px;
    flex-wrap: wrap; align-items: center;
  }
  .pc-session-meta span { display: flex; align-items: center; gap: 3px; }
  .pc-status-chip {
    font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
    padding: 2px 8px; border-radius: 10px; border: 1px solid;
  }
  .pc-ics-btn {
    background: none; border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px;
    cursor: pointer; display: flex; align-items: center; gap: 4px; font-size: 12px;
    color: var(--ink-mid); transition: border-color 0.15s, color 0.15s; white-space: nowrap;
  }
  .pc-ics-btn:hover { border-color: var(--teal); color: var(--teal); }
  .pc-ics-btn.primary { background: var(--teal); border-color: var(--teal); color: #fff; }
  .pc-ics-btn.primary:hover { color: #fff; opacity: 0.92; }
  .pc-empty { text-align: center; padding: 22px 16px; color: var(--ink-mid); font-size: 13px; }
  .pc-collapse { display: none; }
  @media (max-width: 760px) {
    .pc-collapse {
      display: flex; align-items: center; gap: 6px; background: var(--white);
      border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px;
      font-size: 13px; color: var(--ink-mid); cursor: pointer;
    }
    .pc-month-label { min-width: 0; }
  }
`;

export default function BookingsCalendar({ refreshKey, onOpenBooking, onSchedule }) {
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [feedUrl, setFeedUrl] = useState(null);
  const [feedCopied, setFeedCopied] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [connectedCalendars, setConnectedCalendars] = useState([]);
  // On a phone the month fills the screen before a single request is in sight, so it starts
  // folded there and the ribbon below is what they land on.
  const [gridOpen, setGridOpen] = useState(
    () => !(typeof window !== "undefined" && window.matchMedia?.("(max-width: 760px)").matches)
  );

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // The month being looked at, not a page of history: a flat page returns the oldest bookings
  // and hides every upcoming session once a practitioner has more than a hundred.
  useEffect(() => {
    const from = new Date(year, month - 1, 1);
    const to = new Date(year, month + 2, 0, 23, 59, 59);
    apiFetch(`/practitioner/bookings?per_page=500&from_date=${from.toISOString()}`
             + `&to_date=${to.toISOString()}`)
      .then((d) => setBookings(d?.bookings || d || []))
      .catch(() => {});
  }, [year, month, refreshKey]);

  useEffect(() => {
    // Subscribing to the feed AND connecting Google/Outlook writes every session twice,
    // which reads as a double booking.
    apiFetch("/calendar/status")
      .then(st => setConnectedCalendars(
        Object.values(st || {}).filter(c => c.connected).map(c => c.label)))
      .catch(() => {});
  }, []);

  const generateFeed = async () => {
    try {
      const res = await apiFetch("/practitioner/calendar-feed", { method: "POST" });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      setFeedUrl(`${baseUrl}/api/cal/${res.feed_token}.ics`);
      setShowSync(true);
    } catch {
      alert("Failed to generate calendar feed");
    }
  };

  const copyFeedUrl = () => {
    navigator.clipboard.writeText(feedUrl);
    setFeedCopied(true);
    setTimeout(() => setFeedCopied(false), 2000);
  };

  const bookingsByDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (b.status === "cancelled") continue;
      const key = formatDateKey(new Date(b.scheduled_at));
      if (!map[key]) map[key] = [];
      map[key].push(b);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    }
    return map;
  }, [bookings]);

  const calendarCells = useMemo(() => {
    const lastDay = new Date(year, month + 1, 0);
    const startPad = new Date(year, month, 1).getDay();
    const cells = [];
    const prevMonthLast = new Date(year, month, 0);
    for (let i = startPad - 1; i >= 0; i--) {
      cells.push({ date: new Date(year, month - 1, prevMonthLast.getDate() - i), otherMonth: true });
    }
    for (let day = 1; day <= lastDay.getDate(); day++) {
      cells.push({ date: new Date(year, month, day), otherMonth: false });
    }
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
      cells.push({ date: new Date(year, month + 1, i), otherMonth: true });
    }
    return cells;
  }, [year, month]);

  const todayKey = formatDateKey(new Date());
  const selectedKey = selectedDate ? formatDateKey(selectedDate) : null;
  const selectedBookings = selectedKey ? (bookingsByDate[selectedKey] || []) : [];
  const monthCount = bookings.filter((b) => {
    const d = new Date(b.scheduled_at);
    return b.status !== "cancelled" && d.getFullYear() === year && d.getMonth() === month;
  }).length;

  const pickDay = (date) => {
    setSelectedDate(date);
    if (!gridOpen) setGridOpen(true);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="pc-wrap" data-testid="practitioner-calendar">
        <div className="pc-bar">
          <div className="pc-nav">
            <button className="pc-nav-btn" onClick={prevMonthFor(setCurrentDate, year, month)}
              data-testid="calendar-prev-btn" aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <span className="pc-month-label" data-testid="calendar-month-label">
              {MONTHS[month]} {year}
            </span>
            <button className="pc-nav-btn" onClick={nextMonthFor(setCurrentDate, year, month)}
              data-testid="calendar-next-btn" aria-label="Next month">
              <ChevronRight size={16} />
            </button>
            <button className="pc-today-btn" data-testid="calendar-today-btn"
              onClick={() => { setCurrentDate(new Date()); setSelectedDate(new Date()); }}>
              Today
            </button>
          </div>
          <div className="pc-nav">
            <span style={{ fontSize: 12, color: "#5a5a54" }} data-testid="calendar-month-count">
              {monthCount} session{monthCount === 1 ? "" : "s"} this month
            </span>
            <button className="pc-collapse" onClick={() => setGridOpen(v => !v)}
              data-testid="calendar-collapse-btn">
              <CalendarIcon size={13} /> {gridOpen ? "Hide month" : "Show month"}
            </button>
            <button className="pc-nav-btn" onClick={generateFeed} data-testid="calendar-sync-btn"
              title="Sync to external calendar">
              <Link2 size={14} /> Sync
            </button>
          </div>
        </div>

        {showSync && feedUrl && (
          <div style={{ padding: 14, borderBottom: "1px solid #e4ddd4" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontFamily: "'Literata', serif", fontSize: 15, fontWeight: 600, color: "#1a1a16" }}>
                Calendar Subscription
              </span>
              <button onClick={() => setShowSync(false)} style={{
                background: "none", border: "none", cursor: "pointer", color: "#8a8a80", padding: 4,
              }} data-testid="calendar-sync-close">&times;</button>
            </div>
            <p style={{ fontSize: 12, color: "#5a5a54", marginBottom: 10 }}>
              Copy this URL and paste it into your calendar app to <strong>see</strong> your sessions
              there. It is one-way and read-only: your calendar cannot block times here, and most
              calendar apps only check a subscription every few hours, so changes show up late.
              To stop double-bookings and have sessions appear the moment you confirm them,
              connect your calendar in <strong>Availability → Limits &amp; Calendar</strong> instead.
            </p>
            {connectedCalendars.length > 0 && (
              <p style={{
                fontSize: 12, color: "#7a5200", background: "#fdf6e3", border: "1px solid #efdca8",
                borderRadius: 8, padding: "10px 12px", marginBottom: 10, lineHeight: 1.6,
              }} data-testid="calendar-feed-duplicate-warning">
                You have already connected {connectedCalendars.join(" and ")}, so your sessions are
                written there directly. Subscribing to this feed as well would show every session
                twice — use one or the other.
              </p>
            )}
            <div style={{
              display: "flex", gap: 8, alignItems: "center", background: "#faf7f2",
              border: "1px solid #e4ddd4", borderRadius: 6, padding: "8px 10px",
            }}>
              <input readOnly value={feedUrl} style={{
                flex: 1, background: "transparent", border: "none", fontSize: 12,
                fontFamily: "monospace", color: "#1a1a16", outline: "none",
              }} data-testid="calendar-feed-url" />
              <button onClick={copyFeedUrl} style={{
                background: feedCopied ? "#1a5a44" : "#fff", border: "1px solid #e4ddd4",
                borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 12,
                display: "flex", alignItems: "center", gap: 4,
                color: feedCopied ? "#fff" : "#5a5a54",
              }} data-testid="copy-feed-url-btn">
                {feedCopied ? <><CheckIcon size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: "#8a8a80" }}>
              <strong>Google Calendar:</strong> Settings &rarr; Other calendars &rarr; From URL &rarr; Paste link<br />
              <strong>Apple Calendar:</strong> File &rarr; New Calendar Subscription &rarr; Paste link<br />
              <strong>Outlook:</strong> Add calendar &rarr; Subscribe from web &rarr; Paste link
            </div>
          </div>
        )}

        {gridOpen && (
          <>
            <div className="pc-legend">
              {Object.entries(STATUS_COLORS).filter(([k]) => k !== "cancelled").map(([status, c]) => (
                <div key={status} className="pc-legend-item">
                  <span className="pc-cell-dot" style={{ background: c.border }} />
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </div>
              ))}
            </div>

            <div className="pc-grid-wrap">
              <div className="pc-day-headers">
                {DAYS.map(d => <div key={d} className="pc-day-header">{d}</div>)}
              </div>
              <div className="pc-grid" data-testid="calendar-grid">
                {calendarCells.map((cell, i) => {
                  const key = formatDateKey(cell.date);
                  const daySessions = bookingsByDate[key] || [];
                  return (
                    <div
                      key={i}
                      className={`pc-cell${cell.otherMonth ? " other-month" : ""}`
                        + `${key === todayKey ? " today" : ""}`
                        + `${key === selectedKey ? " selected" : ""}`}
                      onClick={() => !cell.otherMonth && pickDay(cell.date)}
                      data-testid={`calendar-cell-${key}`}
                    >
                      <div className="pc-cell-num">{cell.date.getDate()}</div>
                      {daySessions.slice(0, 3).map((s, j) => {
                        const sc = STATUS_COLORS[s.status] || STATUS_COLORS.requested;
                        return (
                          <div
                            key={j}
                            className="pc-cell-session"
                            style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}
                            onClick={(e) => { e.stopPropagation(); pickDay(cell.date); onOpenBooking(s); }}
                            data-testid={`calendar-chip-${s.id}`}
                          >
                            {formatTime(s.scheduled_at)} {s.session_title}
                          </div>
                        );
                      })}
                      {daySessions.length > 3 && (
                        <div className="pc-cell-more">+{daySessions.length - 3} more</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {selectedDate && (
          <div className="pc-detail" data-testid="calendar-detail-panel">
            <div className="pc-detail-header">
              <span>
                {selectedDate.toLocaleDateString("en-US",
                  { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </span>
              <button className="pc-ics-btn primary" data-testid="calendar-schedule-btn"
                onClick={() => onSchedule(selectedKey)}>
                <CalendarPlus size={13} /> Schedule someone on this day
              </button>
            </div>
            {selectedBookings.length === 0 ? (
              <div className="pc-empty" data-testid="calendar-day-empty">
                Nothing booked on this day.
              </div>
            ) : (
              selectedBookings.map(b => {
                const sc = STATUS_COLORS[b.status] || STATUS_COLORS.requested;
                const clientName = b.guest_name || b.customer_name || "Client";
                return (
                  <div key={b.id} className="pc-session-card" data-testid={`session-card-${b.id}`}>
                    <div className="pc-session-time">{formatTime(b.scheduled_at)}</div>
                    <div className="pc-session-info">
                      <div className="pc-session-title">{b.session_title}</div>
                      <div className="pc-session-meta">
                        <span>{b.source === "widget" ? <Globe size={11} /> : <User size={11} />} {clientName}</span>
                        <span><Clock size={11} /> {b.duration_minutes || 60} min</span>
                        {b.price > 0 && <span>${b.price}</span>}
                        <span className="pc-status-chip"
                          style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>
                          {b.status}
                        </span>
                      </div>
                    </div>
                    <button className="pc-ics-btn" onClick={() => onOpenBooking(b)}
                      data-testid={`calendar-open-booking-${b.id}`}>
                      Open below
                    </button>
                    <button
                      className="pc-ics-btn"
                      onClick={(e) => { e.stopPropagation(); generateICS(b); }}
                      title="Download .ics file to add to your calendar"
                      data-testid={`add-to-calendar-${b.id}`}
                    >
                      <Download size={13} /> Add to Calendar
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </>
  );
}

const prevMonthFor = (set, year, month) => () => set(new Date(year, month - 1, 1));
const nextMonthFor = (set, year, month) => () => set(new Date(year, month + 1, 1));
